/**
 * operations-alerts.ts — pure mapping + sort + cap layer for the Command
 * Center's Operations rail. The hook in useOperationsAlerts.ts fetches the
 * data; this module turns those raw pillar payloads into a unified, sorted,
 * capped list of `OperationsAlert` rows.
 *
 * Doctrine (per .agents/Tasks/High Priority/operations-priority-alerts-curation.md §3):
 *   - Drift   admit: severity = Critical AND status = Open AND detectedAt > now-14d
 *   - BPA     admit: criticality = SHALL  AND status = Open
 *   - Identity admit: critical-only signals (GA without MFA, dormant GAs)
 *   - Sort   : descending by detectedAt; tie-break crit > high > med, then
 *              Identity > Compliance > BPA
 *   - Cap    : 8 visible rows (overflow surfaces in `8 of N` badge upstream)
 *
 * Voice rules: no em-dashes (mid-sentence "—"), subject-led titles, ≤6 words
 * where practical. Empty-state copy lives in the consuming component.
 *
 * The mappers are pure and synchronous — no React, no fetches. The hook
 * composes them with the React Query cache.
 */

import {
  DriftSeverity,
  type ConfigurationDrift,
  type UserInsightsSummary,
} from '@/types'
import type { ComplianceViolation } from '@/types/standardsCompliance'

/* -------------------------------------------------------------------------- */
/* Public types                                                               */
/* -------------------------------------------------------------------------- */

export type OperationsAlertSource = 'drift' | 'shall' | 'identity'
export type OperationsAlertPillar = 'compliance' | 'bpa' | 'identity'
/**
 * Phase 1 always emits 'crit'. The wider union exists so callers (and the
 * Alert sub-component) can keep accepting a tone arg today and we can relax
 * the rail's severity floor in Phase 2 without a refactor.
 */
export type OperationsAlertSeverity = 'crit' | 'high' | 'med'

export interface OperationsAlert {
  id: string
  source: OperationsAlertSource
  pillar: OperationsAlertPillar
  severity: OperationsAlertSeverity
  /** Subject-led headline, e.g. "SharingCapability changed on 7 SP sites". */
  text: string
  /** Optional secondary clause. Rendered in muted weight under `text`. */
  detail?: string
  /**
   * ISO timestamp from the source. Drift + Shall rows carry the real
   * detectedAt; Identity rows omit this — they're states, not events, and
   * faking a timestamp would mislead operators.
   */
  detectedAt?: string
  href: string
  /** Free-form label: "Compliance · Critical", "Best Practice · Shall", … */
  sourceLabel: string
}

/* -------------------------------------------------------------------------- */
/* Tunables                                                                   */
/* -------------------------------------------------------------------------- */

export const OPERATIONS_ALERT_CAP = 8
const DAY_MS = 24 * 60 * 60 * 1000
export const DRIFT_RECENCY_WINDOW_MS = 14 * DAY_MS

/* Identity > Compliance > BPA tie-break order. */
const PILLAR_TIEBREAK: Record<OperationsAlertPillar, number> = {
  identity: 0,
  compliance: 1,
  bpa: 2,
}

/* crit > high > med tie-break order. */
const SEVERITY_TIEBREAK: Record<OperationsAlertSeverity, number> = {
  crit: 0,
  high: 1,
  med: 2,
}

/* -------------------------------------------------------------------------- */
/* Drift mapper                                                               */
/* -------------------------------------------------------------------------- */

export interface MapDriftsOptions {
  /** Allow tests to pin a now() so 14-day windows are deterministic. */
  now?: number
}

/**
 * Map ConfigurationDrift rows to OperationsAlert rows.
 *
 * Admits only Critical severity that's still Open and was detected within
 * the last 14 days. High / Medium / Low drifts belong on the Posture queue
 * (they get ranked by impact there); admitting them here would blur the
 * rail's "what should I notice today" purpose.
 */
export function mapDriftsToAlerts(
  drifts: ReadonlyArray<ConfigurationDrift>,
  options: MapDriftsOptions = {},
): OperationsAlert[] {
  const now = options.now ?? Date.now()
  const cutoff = now - DRIFT_RECENCY_WINDOW_MS

  const out: OperationsAlert[] = []
  for (const d of drifts) {
    if (d.severity !== DriftSeverity.Critical) continue
    // status="Open" is stricter than the New/Pending/PendingApproval/AwaitingRemediation
    // bundle that ACTIVE_DRIFT_STATUSES queries — we keep all of those because they
    // each represent a drift that's still operationally open (i.e. not resolved,
    // remediated, ignored, or rejected). The active-drifts cache key already
    // excludes the closed states.
    const ts = parseTimestamp(d.detectedAt)
    if (ts === null) continue
    if (ts < cutoff) continue

    const subject = formatDriftSubject(d)
    out.push({
      id: `drift-${d.id}`,
      source: 'drift',
      pillar: 'compliance',
      severity: 'crit',
      text: subject,
      detectedAt: d.detectedAt,
      href: `/configuration-drift?driftId=${encodeURIComponent(d.id)}`,
      sourceLabel: 'Compliance · Critical',
    })
  }
  return out
}

function formatDriftSubject(d: ConfigurationDrift): string {
  // Prefer the human-named resource if the backend supplied one distinct
  // from the bare component string. e.g. "SharingCapability on contoso-team-site"
  // beats "SPOSite · SharingCapability".
  if (d.componentName && d.componentName !== d.component) {
    return `${d.setting} on ${d.componentName}`
  }
  return `${d.component} · ${d.setting}`
}

/* -------------------------------------------------------------------------- */
/* SHALL violation mapper                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Map open SHALL ComplianceViolation rows to OperationsAlert rows.
 *
 * SHOULD / MAY violations are deliberately rejected — they belong on the
 * Posture queue where impact-ranking can lift them. The Operations rail is
 * a criticals-only surface.
 */
export function mapShallToAlerts(
  violations: ReadonlyArray<ComplianceViolation>,
): OperationsAlert[] {
  const out: OperationsAlert[] = []
  for (const v of violations) {
    if (v.criticality !== 'SHALL') continue
    if (v.status !== 'Open') continue

    const subject = v.policyTitle?.trim() || v.policyCode || 'Policy violation'
    const detail = formatViolationDetail(v)
    out.push({
      id: `shall-${v.id}`,
      source: 'shall',
      pillar: 'bpa',
      severity: 'crit',
      text: subject,
      detail,
      detectedAt: v.detectedAt,
      href: `/security?tab=best-practices&violationId=${encodeURIComponent(v.id)}`,
      sourceLabel: 'Best Practice · Shall',
    })
  }
  return out
}

function formatViolationDetail(v: ComplianceViolation): string | undefined {
  // Prefer the affectedResource (specific named target) over the generic
  // category/product label. Avoid surfacing the message field — it tends to
  // be a long sentence rather than a short subject clause.
  if (v.affectedResource) return v.affectedResource
  if (v.product) return v.product
  return undefined
}

/* -------------------------------------------------------------------------- */
/* Identity mapper                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Map UserInsightsSummary aggregate fields to OperationsAlert rows.
 *
 * Emits the two operational identity signals the curation doctrine lists as
 * critical: Global Admins without MFA, and dormant Global Admin accounts.
 * Other identity findings (license waste, generic inactive members,
 * phish-resistant gap) are advisory-shaped — they belong on the Posture
 * queue where impact ranking is the right lens.
 *
 * Both rows now read GA-scoped server-side aggregates
 * (`noMfaGlobalAdmins`, `inactiveGlobalAdmins`) populated alongside the
 * existing directory counts in `UserInsightsService.GetCachedInsightsAsync`.
 * Tenants whose last scan predates the `IsGlobalAdmin` flag report 0 on
 * both fields, so the rail no-ops on stale data instead of fabricating
 * scope claims.
 */
export function mapIdentityToAlerts(
  insights: UserInsightsSummary | null | undefined,
): OperationsAlert[] {
  if (!insights) return []
  const out: OperationsAlert[] = []

  // ── Global Admins without MFA ─────────────────────────────────────────
  if (insights.noMfaGlobalAdmins > 0) {
    const n = insights.noMfaGlobalAdmins
    out.push({
      id: 'identity-ga-no-mfa',
      source: 'identity',
      pillar: 'identity',
      severity: 'crit',
      text: `${n} Global Admin${plural(n)} without MFA`,
      href: '/user-insights?tab=mfa&scope=globalAdmins',
      sourceLabel: 'Identity · Critical',
    })
  }

  // ── Dormant Global Admins ─────────────────────────────────────────────
  if (insights.inactiveGlobalAdmins > 0) {
    const n = insights.inactiveGlobalAdmins
    out.push({
      id: 'identity-ga-dormant',
      source: 'identity',
      pillar: 'identity',
      severity: 'crit',
      text: `${n} dormant Global Admin account${plural(n)}`,
      href: '/user-insights?tab=inactive&scope=globalAdmins',
      sourceLabel: 'Identity · Critical',
    })
  }

  return out
}

function plural(n: number): string {
  return n === 1 ? '' : 's'
}

/* -------------------------------------------------------------------------- */
/* Merge / sort / cap                                                         */
/* -------------------------------------------------------------------------- */

export interface MergeOptions {
  cap?: number
}

/**
 * Merge per-source alert lists, sort by recency, then apply pillar/severity
 * tie-breaks, and cap at `OPERATIONS_ALERT_CAP` (default).
 *
 * Sort contract:
 *   1. Most recent detectedAt first.
 *   2. Identity rows (no detectedAt) sort to the top within their tie-break
 *      bucket — they represent active states and are always "now".
 *   3. Tie on timestamp: severity tier (crit > high > med).
 *   4. Tie on severity: pillar order (Identity > Compliance > BPA).
 *   5. Final tiebreak: stable insert order via id.
 */
export function mergeSortAndCap(
  buckets: ReadonlyArray<ReadonlyArray<OperationsAlert>>,
  options: MergeOptions = {},
): OperationsAlert[] {
  const cap = options.cap ?? OPERATIONS_ALERT_CAP

  const merged: OperationsAlert[] = []
  for (const b of buckets) merged.push(...b)

  merged.sort((a, b) => {
    // Identity rows have no timestamp. Treat them as "now" so they sort
    // alongside the freshest events; the severity + pillar tie-breaks
    // below still order them sensibly against same-timestamp drift rows.
    const ta = a.detectedAt ? Date.parse(a.detectedAt) : Number.POSITIVE_INFINITY
    const tb = b.detectedAt ? Date.parse(b.detectedAt) : Number.POSITIVE_INFINITY
    const taSafe = Number.isNaN(ta) ? 0 : ta
    const tbSafe = Number.isNaN(tb) ? 0 : tb
    if (tbSafe !== taSafe) return tbSafe - taSafe

    const sevCmp = SEVERITY_TIEBREAK[a.severity] - SEVERITY_TIEBREAK[b.severity]
    if (sevCmp !== 0) return sevCmp

    const pillarCmp = PILLAR_TIEBREAK[a.pillar] - PILLAR_TIEBREAK[b.pillar]
    if (pillarCmp !== 0) return pillarCmp

    return a.id.localeCompare(b.id)
  })

  return merged.slice(0, cap)
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function parseTimestamp(iso: string | null | undefined): number | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return null
  return t
}

/* -------------------------------------------------------------------------- */
/* Composition contract used by the hook                                      */
/* -------------------------------------------------------------------------- */

export interface ComposeOperationsAlertsInput {
  drifts:     ReadonlyArray<ConfigurationDrift>
  violations: ReadonlyArray<ComplianceViolation>
  insights:   UserInsightsSummary | null | undefined
  /** Test-only: pin now() so the 14-day window is deterministic. */
  now?:       number
}

export interface ComposeOperationsAlertsResult {
  alerts:           OperationsAlert[]
  totalCandidates:  number
  /**
   * Per-source admit-list candidate counts (pre-cap). The sum equals
   * `totalCandidates`. Useful for diagnostics; the overflow footer reads
   * `hiddenCountsBySource` instead so the labels reflect what's actually
   * hidden behind the cap.
   */
  candidateCountsBySource: { drift: number; shall: number; identity: number }
  /**
   * Per-source HIDDEN counts: pre-cap candidates minus the rows the rail
   * actually surfaced. Drives the criticals overflow footer's per-source
   * deep links so the labels promise only what's hidden, never double-
   * counting rows already visible in the rail. The sum equals
   * `totalCandidates - alerts.length`.
   */
  hiddenCountsBySource: { drift: number; shall: number; identity: number }
}

/**
 * Pure composition of the three pillar inputs into a curated, sorted,
 * capped list of alerts. The hook layer wires data fetching; this is the
 * deterministic shape transform the hook delegates to.
 */
export function composeOperationsAlerts(
  input: ComposeOperationsAlertsInput,
): ComposeOperationsAlertsResult {
  const driftAlerts    = mapDriftsToAlerts(input.drifts, { now: input.now })
  const shallAlerts    = mapShallToAlerts(input.violations)
  const identityAlerts = mapIdentityToAlerts(input.insights)
  const candidateCountsBySource = {
    drift:    driftAlerts.length,
    shall:    shallAlerts.length,
    identity: identityAlerts.length,
  }
  const totalCandidates =
    candidateCountsBySource.drift +
    candidateCountsBySource.shall +
    candidateCountsBySource.identity
  const alerts = mergeSortAndCap([driftAlerts, shallAlerts, identityAlerts])

  // Count visible per source on the post-cap list, then subtract from the
  // candidate counts. This is the honest "hidden" tally — the footer's
  // labels need to refer to rows the user can't already see in the rail.
  const visibleBySource = { drift: 0, shall: 0, identity: 0 }
  for (const a of alerts) {
    if (a.source === 'drift') visibleBySource.drift++
    else if (a.source === 'shall') visibleBySource.shall++
    else visibleBySource.identity++
  }
  const hiddenCountsBySource = {
    drift:    candidateCountsBySource.drift    - visibleBySource.drift,
    shall:    candidateCountsBySource.shall    - visibleBySource.shall,
    identity: candidateCountsBySource.identity - visibleBySource.identity,
  }

  return {
    alerts,
    totalCandidates,
    candidateCountsBySource,
    hiddenCountsBySource,
  }
}
